import { useCallback, useEffect, useState } from "react";
import Papa from "papaparse";
import { useSDK } from "../lib/sdk-context";
import { api } from "../lib/api";
import ColumnMapper from "../components/ColumnMapper";

interface Props { kind: "cm" | "wtb"; }

export default function Upload({ kind }: Props) {
  const { sdk, context } = useSDK();
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [brandDomain, setBrandDomain] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [serverMapping, setServerMapping] = useState<Record<string, string> | undefined>(undefined);

  useEffect(() => {
    // Load saved mapping for this brand/kind so re-uploads remember.
    const key = kind === "cm" ? "cm" : "wtb:hoodie";
    (async () => {
      try {
        const me = await api.get(context, "/api/brand/me");
        if (me?.mappings?.[key]) setServerMapping(me.mappings[key]);
      } catch {}
    })();
  }, [context, kind]);

  const handleFile = useCallback((f: File) => {
    setFile(f);
    Papa.parse<Record<string, string>>(f, {
      header: true, skipEmptyLines: true, dynamicTyping: false,
      complete: (out) => {
        const hdrs = (out.meta.fields as string[]) || [];
        setHeaders(hdrs);
        setRows(out.data as Record<string, string>[]);
      },
    });
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const submit = async () => {
    setSubmitting(true);
    setMsg(null);
    try {
      // Ensure project exists for this upload session (first time only).
      if (sdk && context) {
        try { await sdk.createProject(`${kind.toUpperCase()} upload ${new Date().toLocaleString()}`, "cannabest-storesync"); } catch {}
      }
      if (kind === "cm") {
        const r = await api.post(context, "/api/upload/cm", { headers, rows, mapping });
        setMsg(`Uploaded ${r.rows} rows. Running match…`);
        const m = await api.post(context, "/api/match", { type: "cm" });
        setMsg(`Match complete: ${m.counts.auto} auto, ${m.counts.review} review, ${m.counts.unmatched} unmatched.`);
      } else {
        const r = await api.post(context, "/api/upload/wtb", {
          provider_slug: "hoodie", headers, rows, mapping, brand_domain: brandDomain || undefined,
        });
        setMsg(`Uploaded ${r.rows} dispensaries. Running match…`);
        const m = await api.post(context, "/api/match", { type: "wtb", provider_slug: "hoodie" });
        setMsg(`Match complete: ${m.counts.auto} auto, ${m.counts.review} review, ${m.counts.unmatched} unmatched.`);
      }
      setTimeout(() => { window.location.hash = "#/upload"; }, 800);
    } catch (err: any) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const missingRequired =
    kind === "cm"
      ? !mapping.license_number || !mapping.name
      : !mapping.provider_dispensary_id || !mapping.provider_dispensary_name;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">
          {kind === "cm" ? "Upload Customer Master CSV" : "Upload Where-to-Buy CSV (Hoodie)"}
        </h2>
        <p className="text-sm text-slate-600">
          {kind === "cm"
            ? "One row per retailer account in your customer master. We match each license against the California DCC graph."
            : "Hoodie export — one row per dispensary location. We store Carried SKUs, Monthly Sales, and SKU Count Change verbatim."}
        </p>
      </div>

      {!file && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="card p-10 border-dashed border-2 text-center text-slate-500"
        >
          <p className="mb-3">Drag and drop a CSV here</p>
          <label className="btn-primary cursor-pointer inline-block">
            Choose file
            <input type="file" accept=".csv,text/csv" className="hidden"
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          </label>
        </div>
      )}

      {file && (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            <span className="font-medium">{file.name}</span> — {rows.length.toLocaleString()} rows, {headers.length} columns.
            <button className="ml-3 text-slate-400 underline" onClick={() => { setFile(null); setRows([]); setHeaders([]); }}>change file</button>
          </div>

          {kind === "wtb" && (
            <div className="card p-4">
              <label className="text-sm font-medium">Brand domain (for Hoodie deep-link template)</label>
              <input
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1"
                placeholder="e.g. rawgarden.farm"
                value={brandDomain}
                onChange={(e) => setBrandDomain(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Used to build <code>{`https://{brand_domain}/find?hp_dispensary_id={id}`}</code>.
              </p>
            </div>
          )}

          <ColumnMapper
            kind={kind}
            headers={headers}
            initialMapping={serverMapping}
            onChange={setMapping}
          />

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {missingRequired ? "Fill required columns to enable upload." : "Ready to upload."}
            </div>
            <button className="btn-primary" disabled={submitting || missingRequired} onClick={submit}>
              {submitting ? "Uploading…" : "Upload and match"}
            </button>
          </div>

          {msg && <div className="card p-3 text-sm">{msg}</div>}
        </div>
      )}
    </div>
  );
}
